/**
 * ListGridCard - Card tile with quantity badge and hover stepper
 */
import type { ReactElement } from 'react';

import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import { FaTimes } from 'react-icons/fa';

import CardHoverPreview from '@/components/CardHoverPreview/CardHoverPreview';
import FlippableCardImage from '@/components/FlippableCardImage/FlippableCardImage';
import QuantityStepper from '@/components/QuantityStepper/QuantityStepper';
import CardSelectOverlayButton from '@/features/decks/components/CardSelectOverlayButton';
import { fullCardCacheAtom } from '@/state/cardCache';
import type { EnrichedListCard } from '@/types/lists';

export interface ListGridCardProps {
  card: EnrichedListCard;
  isPending: boolean;
  onIncrease: () => void;
  onDecrease: () => void;
  onRemove: () => void;
  onOpenDetails: () => void;
}

export default function ListGridCard({
  card,
  isPending,
  onIncrease,
  onDecrease,
  onRemove,
  onOpenDetails,
}: ListGridCardProps): ReactElement {
  const fullCard = useAtomValue(fullCardCacheAtom).get(card.scryfall_id);

  return (
    <div className={clsx('group relative', isPending && 'pointer-events-none opacity-60')}>
      <CardHoverPreview imageUris={card.sample_image_uris} name={card.name} layout={fullCard?.layout} cardFaces={fullCard?.card_faces}>
        <div
          className={clsx(
            'relative overflow-hidden rounded-xl border-2 transition-transform',
            'hover:scale-[1.02] hover:shadow-lg hover:shadow-black/30',
            card.available_quantity > 0 ? 'border-emerald-500/40' : 'border-transparent',
          )}
        >
          <FlippableCardImage
            imageUris={card.sample_image_uris}
            layout={fullCard?.layout}
            cardFaces={fullCard?.card_faces}
            name={card.name}
            size="normal"
            className="h-auto! w-full!"
          />

          {card.quantity > 1 && (
            <div className="pointer-events-none absolute top-2 right-2 flex h-7 w-7 items-center justify-center rounded-full bg-zinc-900/90 text-sm font-bold text-zinc-100 shadow-md">
              {card.quantity}
            </div>
          )}

          <CardSelectOverlayButton cardName={card.name} onSelect={onOpenDetails} rounded="rounded-xl" />

          {/* Hover controls */}
          <div className="pointer-events-none invisible absolute inset-0 flex flex-col justify-end bg-linear-to-t from-black/80 via-black/20 to-transparent p-3 opacity-0 transition-opacity group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100">
            <div className="pointer-events-auto flex items-center justify-between">
              <QuantityStepper
                size="md"
                value={card.quantity}
                onIncrease={onIncrease}
                onDecrease={onDecrease}
                disabled={isPending}
                className="rounded-lg bg-zinc-900/80 px-1"
                increaseLabel={`Increase quantity of ${card.name}`}
                decreaseLabel={card.quantity <= 1 ? `Remove ${card.name} from list` : `Decrease quantity of ${card.name}`}
              />
              <button
                type="button"
                onClick={onRemove}
                disabled={isPending}
                className="focus-visible:ring-aether rounded-lg bg-zinc-900/80 p-2 text-zinc-400 transition-colors hover:text-red-400 focus:outline-none focus-visible:ring-2"
                aria-label={`Remove ${card.name} from list`}
              >
                <FaTimes className="h-3 w-3" />
              </button>
            </div>
          </div>
        </div>
      </CardHoverPreview>
    </div>
  );
}
